/**
 * The parts picker's model — docs/plans/2026-09-27-editor-rework.md §4.
 *
 * PCPartPicker's product list for this catalogue: per category, the spec
 * columns worth comparing at a glance and the filters that narrow the list.
 * Pure over `Product` — no DOM, no React, no room maths.
 *
 * The columns were chosen from the shipped data (175 products, 2026-09-27)
 * and every one of them VARIES within its category. A column that reads the
 * same on every row is noise — every TV is 4K with four HDMI inputs and
 * eARC, every speaker is passive, every receiver is rated with two channels
 * driven — so none of those is here, and `picker.test.ts` fails the day a
 * registered column stops varying.
 *
 * What depends on the room (a speaker's "Needs", a projector's "Image", what
 * a slot costs when a model is sold in pairs) arrives through
 * `PickerContext` as functions the web computes with @roomtreatment/av-compat.
 * This package is a dependency-free leaf and those formulas live there once
 * (`wattsNeeded`, `screenWidthRangeM`, `listPriceUsd`).
 *
 * `Record<CategoryKey, …>`: a category added to browse.ts without a picker
 * entry is a compile error (AGENTS.md registry rule).
 */
import type { Port, Product, ScreenAspect, ScreenFormat } from './schema';
import { categoryByKey, type CategoryKey } from './browse';
import { searchCatalog } from './search';

export type PickerUnits = 'metric' | 'imperial';

export interface NumRange {
  min: number;
  max: number;
}

/** What a column or filter may know beyond the product itself. */
export interface PickerContext {
  units: PickerUnits;
  /** What `p` costs for the slot being filled (a pair-sold model bought for
   *  one center speaker still costs the pair). Absent: the list price. */
  priceUsd?: (p: Product) => number;
  /** Watts per channel `p` needs to reach the room's peak level at the seat
   *  it would serve; null when `p` publishes no sensitivity. Absent: the room
   *  cannot say (no seat). */
  needsW?: (p: Product) => number | null;
  /** The 16:9 picture diagonal, inches, a projector fills from the back of
   *  the room (one number for a fixed lens, a range for a zoom). Absent: the
   *  room cannot say. */
  imageIn?: (p: Product) => NumRange | null;
  /** Whether `p` works with the rest of the system — the caller's
   *  compatibility engine (av-compat). false: the "fits" filter hides it;
   *  null: nothing to judge it against. Absent: nothing is judged. */
  fits?: (p: Product) => boolean | null;
}

export type SortValue = number | string | null;

export interface PickerColumn {
  id: string;
  /** Header text. */
  label: string;
  /** What the column means — the header's tooltip. */
  describe?: string;
  /** Sort key. null: not published (or not knowable), always sorted last. */
  value: (p: Product, ctx: PickerContext) => SortValue;
  /** Cell text. `NOT_PUBLISHED` when `value` is null. */
  format: (p: Product, ctx: PickerContext) => string;
  align: 'start' | 'end';
  /** 1: always shown. 2: hidden in a narrow table. 3: only in a wide one. */
  priority: 1 | 2 | 3;
  /** Which end is better, so a first click on the header shows the best first
   *  (lowest bass extension, most power). null: neither (a type, a size). */
  better: 'low' | 'high' | null;
  /** A room-aware column's reason it is empty for this room, or null when
   *  it can say. */
  unavailable?: (ctx: PickerContext) => string | null;
}

interface FilterBase {
  id: string;
  label: string;
}

export interface RangeFilterDef extends FilterBase {
  kind: 'range';
  value: (p: Product, ctx: PickerContext) => number | null;
  /** The slider's readout for one end. */
  format: (v: number) => string;
  /** Keyboard increment; also the grain bounds are rounded out to. */
  step: number;
  /** Map the slider logarithmically — prices span two orders of magnitude,
   *  and on a linear track every speaker under $2,000 shares its first 15%. */
  log?: boolean;
}

export interface SetFilterDef extends FilterBase {
  kind: 'set';
  /** The product's option key; null: not published. */
  value: (p: Product) => string | null;
  optionLabel: (key: string) => string;
  /** Option order: ascending by this rank (alphabetical when absent). */
  rank?: (key: string) => number;
}

export interface ToggleFilterDef extends FilterBase {
  kind: 'toggle';
  /** While the toggle is on, only products this is true for stay. */
  test: (p: Product) => boolean;
}

export type PickerFilterDef = RangeFilterDef | SetFilterDef | ToggleFilterDef;

export interface PickerCategory {
  key: CategoryKey;
  /** "98 speakers": singular, plural. */
  noun: readonly [string, string];
  columns: readonly PickerColumn[];
  filters: readonly PickerFilterDef[];
  /** The four columns a phone card shows, in order. */
  phoneColumns: readonly string[];
}

export interface PickerFilterState {
  query: string;
  /** PCPartPicker's compatibility filter: hide what `ctx.fits` says does
   *  not fit. On by default — it hides nothing until something is judged. */
  fitsOnly: boolean;
  /** Narrowed ranges only; an absent id is the full range. */
  ranges: Readonly<Record<string, readonly [number, number]>>;
  /** Checked options; an absent or empty id is every option. */
  sets: Readonly<Record<string, readonly string[]>>;
  toggles: Readonly<Record<string, boolean>>;
}

export type PickerSortKey = 'name' | 'price' | (string & {});
export type SortDir = 'asc' | 'desc';

// ── Formatting ──────────────────────────────────────────────────────────────

/** A spec the maker does not publish (or a room that cannot say). */
export const NOT_PUBLISHED = '—';

const MM_PER_IN = 25.4;

/** At most `digits` decimals, trailing zeros dropped: 86.5, 87, 1.35. */
function num(v: number, digits = 1): string {
  return String(Number(v.toFixed(digits)));
}

function orDash<T>(v: T | null | undefined, f: (v: T) => string): string {
  return v == null ? NOT_PUBLISHED : f(v);
}

const INT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const USD0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/**
 * A driver's nominal size in inches, as its maker would print it. A size
 * published in inches (and converted to mm by the catalogue author) comes back
 * to its quarter inch exactly — 133 mm is 5.25″, 165 mm is 6.5″. One published
 * in millimetres keeps a decimal instead of being passed off as a round
 * imperial size — 130 mm is 5.1″, 250 mm is 9.8″.
 */
export function driverInches(mm: number): string {
  const inches = mm / MM_PER_IN;
  const quarter = Math.round(inches * 4) / 4;
  const v = Math.abs(inches - quarter) <= 0.03 ? quarter : Math.round(inches * 10) / 10;
  return `${v}″`;
}

/** One body length in cm or whole inches — core's `toPanelDisplay`, to the
 *  same floating-point operations, so a size here reads exactly as
 *  `fmtPanelDims` prints it on the product view (web's partsPicker test pins
 *  the parity; this leaf package cannot import core). */
function panelLen(mm: number, units: PickerUnits): number {
  const m = mm / 1000;
  return Math.round(units === 'imperial' ? m * 39.37007874 : m * 100);
}

/** Width × height × depth, `20×35×27 cm` — the product view's own format. */
export function sizeLabel(p: Pick<Product, 'body'>, units: PickerUnits): string {
  const unit = units === 'imperial' ? 'in' : 'cm';
  return `${panelLen(p.body.w, units)}×${panelLen(p.body.h, units)}×${panelLen(p.body.d, units)} ${unit}`;
}

function volume(p: Pick<Product, 'body'>): number {
  return p.body.w * p.body.h * p.body.d;
}

// ── Spec readers ────────────────────────────────────────────────────────────

/** The largest bass driver: a woofer or coaxial; for a height module with
 *  neither, its largest up-firing or midrange driver (that driver IS its
 *  woofer). null for anything that is not a speaker. */
export function wooferMm(p: Product): number | null {
  if (p.specs.kind !== 'speaker') return null;
  const drivers = p.specs.drivers.v;
  const bass = drivers.filter((d) => d.role === 'woofer' || d.role === 'coaxial');
  const pool = bass.length > 0 ? bass : drivers.filter((d) => d.role === 'upfiring' || d.role === 'midrange');
  return pool.length > 0 ? Math.max(...pool.map((d) => d.nominalMm)) : null;
}

/** A subwoofer's bass drivers as one key: `305x1`, `229x2`. */
function subDriverKey(p: Product): string | null {
  if (p.specs.kind !== 'subwoofer') return null;
  const woofers = p.specs.drivers.v.filter((d) => d.role === 'woofer' || d.role === 'coaxial');
  const pool = woofers.length > 0 ? woofers : p.specs.drivers.v;
  const d = pool.reduce((a, b) => (b.nominalMm > a.nominalMm ? b : a));
  return `${d.nominalMm}x${pool.filter((x) => x.nominalMm === d.nominalMm).reduce((n, x) => n + x.count, 0)}`;
}

function parseDriverKey(key: string): { mm: number; count: number } {
  const [mm, count] = key.split('x').map(Number);
  return { mm: mm!, count: count! };
}

function driverKeyLabel(key: string): string {
  const { mm, count } = parseDriverKey(key);
  return count > 1 ? `${count} × ${driverInches(mm)}` : driverInches(mm);
}

/** Cone area, in nominal-diameter² units: how subwoofer drivers compare. */
function driverKeyArea(key: string): number {
  const { mm, count } = parseDriverKey(key);
  return count * mm * mm;
}

export const ENCLOSURE_LABEL: Record<Port, string> = {
  sealed: 'Sealed',
  front: 'Front port',
  rear: 'Rear port',
  side: 'Side port',
  bottom: 'Down-firing port',
  passive_radiator: 'Passive radiator',
  open_back: 'Open back',
};
const ENCLOSURE_ORDER: readonly Port[] = ['sealed', 'front', 'side', 'bottom', 'rear', 'passive_radiator', 'open_back'];

export const SCREEN_FORMAT_LABEL: Record<ScreenFormat, string> = {
  fixed: 'Fixed frame',
  'pull-down': 'Pull-down',
  motorized: 'Motorized',
};
const SCREEN_FORMAT_ORDER: readonly ScreenFormat[] = ['fixed', 'pull-down', 'motorized'];
const ASPECT_ORDER: readonly ScreenAspect[] = ['16:9', '2.35:1'];

const RESOLUTION_RANK: Record<string, number> = { '1080p': 1, '4K': 2, '8K': 3 };

function avChannels(p: Product): { amp: number; proc: number } | null {
  if (p.specs.kind !== 'av') return null;
  return { amp: p.specs.ampChannels.v, proc: p.specs.processingChannels.v };
}

function channelsLabel(amp: number, proc: number): string {
  return amp > 0 ? `${amp} amp · ${proc} proc` : `${proc} proc`;
}

function ampInputsLabel(inputs: { rca: boolean; xlr: boolean }): string {
  return [inputs.rca && 'RCA', inputs.xlr && 'XLR'].filter(Boolean).join(', ') || NOT_PUBLISHED;
}

/** Type label for a type key — browse.ts's labels, one table. */
function typeLabel(category: CategoryKey, type: string): string {
  return categoryByKey(category).types.find((t) => t.type === type)?.label ?? type;
}

function typeRank(category: CategoryKey) {
  const order = categoryByKey(category).types.map((t) => t.type);
  return (type: string) => order.indexOf(type);
}

function fixedRank<T extends string>(order: readonly T[]) {
  return (key: string) => {
    const i = order.indexOf(key as T);
    return i === -1 ? order.length : i;
  };
}

// ── Shared columns and filters ──────────────────────────────────────────────

function typeColumn(category: CategoryKey): PickerColumn {
  return {
    id: 'type',
    label: 'Type',
    value: (p) => typeLabel(category, p.type),
    format: (p) => typeLabel(category, p.type),
    align: 'start',
    priority: 2,
    better: null,
  };
}

function sizeColumn(priority: 1 | 2 | 3): PickerColumn {
  return {
    id: 'size',
    label: 'Size',
    describe: 'Width × height × depth.',
    value: (p) => volume(p),
    format: (p, ctx) => sizeLabel(p, ctx.units),
    align: 'end',
    priority,
    better: null,
  };
}

function lowHzOf(p: Product): number | null {
  if (p.specs.kind === 'speaker') return p.specs.lowHz?.v ?? null;
  if (p.specs.kind === 'subwoofer') return p.specs.lowHz.v;
  return null;
}

const LOW_END_COLUMN: PickerColumn = {
  id: 'lowHz',
  label: 'Low end',
  describe: 'The deepest note it plays. Lower is deeper bass.',
  value: (p) => lowHzOf(p),
  format: (p) => orDash(lowHzOf(p), (v) => `${Math.round(v)} Hz`),
  align: 'end',
  priority: 1,
  better: 'low',
};

/** The price a row shows and sorts by. */
export function pickerPrice(p: Product, ctx: PickerContext): number {
  return ctx.priceUsd ? ctx.priceUsd(p) : p.msrpUsd.v;
}

const PRICE_FILTER: RangeFilterDef = {
  kind: 'range',
  id: 'price',
  label: 'Price',
  value: (p, ctx) => pickerPrice(p, ctx),
  format: (v) => USD0.format(v),
  step: 10,
  log: true,
};

const BRAND_FILTER: SetFilterDef = {
  kind: 'set',
  id: 'brand',
  label: 'Brand',
  value: (p) => p.brand,
  optionLabel: (key) => key,
};

/** Worded for what stays, like every other toggle ("Dolby Atmos", "XLR
 *  inputs"), so its count reads as the models left. */
const CURRENT_FILTER: ToggleFilterDef = {
  kind: 'toggle',
  id: 'current',
  label: 'Current models',
  test: (p) => p.status === 'current',
};

function typeFilter(category: CategoryKey): SetFilterDef {
  return {
    kind: 'set',
    id: 'type',
    label: 'Type',
    value: (p) => p.type,
    optionLabel: (key) => typeLabel(category, key),
    rank: typeRank(category),
  };
}

function lowHzFilter(): RangeFilterDef {
  return {
    kind: 'range', id: 'lowHz', label: 'Low end', value: (p) => lowHzOf(p), format: (v) => `${Math.round(v)} Hz`, step: 1,
  };
}

// ── The registry ────────────────────────────────────────────────────────────

const speakers: PickerCategory = {
  key: 'speakers',
  noun: ['speaker', 'speakers'],
  columns: [
    typeColumn('speakers'),
    LOW_END_COLUMN,
    {
      id: 'sensitivity',
      label: 'Sensitivity',
      describe: 'How loud it plays from one watt. Higher needs less power.',
      value: (p) => (p.specs.kind === 'speaker' ? p.specs.sensitivityDb?.v ?? null : null),
      format: (p) => orDash(p.specs.kind === 'speaker' ? p.specs.sensitivityDb?.v : null, (v) => `${num(v)} dB`),
      align: 'end',
      priority: 3,
      better: 'high',
    },
    {
      id: 'impedance',
      label: 'Impedance',
      describe: 'The load it puts on an amplifier. 4 Ω is the hardest to drive.',
      value: (p) => (p.specs.kind === 'speaker' ? p.specs.impedanceOhm?.v ?? null : null),
      format: (p) => orDash(p.specs.kind === 'speaker' ? p.specs.impedanceOhm?.v : null, (v) => `${num(v)} Ω`),
      align: 'end',
      priority: 2,
      better: 'high',
    },
    {
      id: 'woofer',
      label: 'Woofer',
      describe: 'The largest bass driver.',
      value: (p) => wooferMm(p),
      format: (p) => orDash(wooferMm(p), driverInches),
      align: 'end',
      priority: 3,
      better: 'high',
    },
    {
      id: 'needs',
      label: 'Needs',
      describe: 'Watts per channel to reach your listening level at the seat.',
      value: (p, ctx) => ctx.needsW?.(p) ?? null,
      format: (p, ctx) => orDash(ctx.needsW?.(p), (w) => `${INT.format(w)} W`),
      align: 'end',
      priority: 1,
      better: 'low',
      unavailable: (ctx) => (ctx.needsW ? null : 'Place a seat to see the power each one needs.'),
    },
    sizeColumn(3),
  ],
  filters: [
    PRICE_FILTER,
    typeFilter('speakers'),
    BRAND_FILTER,
    lowHzFilter(),
    {
      kind: 'range',
      id: 'sensitivity',
      label: 'Sensitivity',
      value: (p) => (p.specs.kind === 'speaker' ? p.specs.sensitivityDb?.v ?? null : null),
      format: (v) => `${num(v)} dB`,
      step: 0.5,
    },
    {
      kind: 'set',
      id: 'impedance',
      label: 'Impedance',
      value: (p) => (p.specs.kind === 'speaker' && p.specs.impedanceOhm ? String(p.specs.impedanceOhm.v) : null),
      optionLabel: (key) => `${key} Ω`,
      rank: Number,
    },
    {
      kind: 'set',
      id: 'woofer',
      label: 'Woofer',
      value: (p) => {
        const mm = wooferMm(p);
        return mm == null ? null : driverInches(mm);
      },
      optionLabel: (key) => key,
      rank: (key) => parseFloat(key),
    },
    CURRENT_FILTER,
  ],
  phoneColumns: ['lowHz', 'needs', 'impedance', 'size'],
};

const subwoofers: PickerCategory = {
  key: 'subwoofers',
  noun: ['subwoofer', 'subwoofers'],
  columns: [
    {
      id: 'driver',
      label: 'Driver',
      describe: 'Bass driver size.',
      value: (p) => {
        const key = subDriverKey(p);
        return key ? driverKeyArea(key) : null;
      },
      format: (p) => orDash(subDriverKey(p), driverKeyLabel),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'amp',
      label: 'Amplifier',
      describe: 'Built-in amplifier power.',
      value: (p) => (p.specs.kind === 'subwoofer' ? p.specs.ampW.v : null),
      format: (p) => orDash(p.specs.kind === 'subwoofer' ? p.specs.ampW.v : null, (w) => `${INT.format(w)} W`),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    LOW_END_COLUMN,
    {
      id: 'enclosure',
      label: 'Enclosure',
      describe: 'Sealed, or where the port faces. A rear port needs space behind it.',
      value: (p) => (p.specs.kind === 'subwoofer' ? ENCLOSURE_LABEL[p.specs.port.v] : null),
      format: (p) => orDash(p.specs.kind === 'subwoofer' ? ENCLOSURE_LABEL[p.specs.port.v] : null, (s) => s),
      align: 'start',
      priority: 2,
      better: null,
    },
    sizeColumn(2),
  ],
  filters: [
    PRICE_FILTER,
    BRAND_FILTER,
    {
      kind: 'set',
      id: 'driver',
      label: 'Driver',
      value: subDriverKey,
      optionLabel: driverKeyLabel,
      rank: driverKeyArea,
    },
    {
      kind: 'range',
      id: 'amp',
      label: 'Amplifier',
      value: (p) => (p.specs.kind === 'subwoofer' ? p.specs.ampW.v : null),
      format: (v) => `${INT.format(v)} W`,
      step: 10,
    },
    lowHzFilter(),
    {
      kind: 'set',
      id: 'enclosure',
      label: 'Enclosure',
      value: (p) => (p.specs.kind === 'subwoofer' ? p.specs.port.v : null),
      optionLabel: (key) => ENCLOSURE_LABEL[key as Port] ?? key,
      rank: fixedRank(ENCLOSURE_ORDER),
    },
    CURRENT_FILTER,
  ],
  phoneColumns: ['driver', 'amp', 'lowHz', 'enclosure'],
};

const receivers: PickerCategory = {
  key: 'receivers',
  noun: ['receiver or processor', 'receivers and processors'],
  columns: [
    typeColumn('receivers'),
    {
      id: 'channels',
      label: 'Channels',
      describe: 'Amplified channels · channels it can process with a power amp.',
      value: (p) => {
        const c = avChannels(p);
        return c ? c.amp * 100 + c.proc : null;
      },
      format: (p) => orDash(avChannels(p), (c) => channelsLabel(c.amp, c.proc)),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'power',
      label: 'Power',
      describe: 'Watts per channel into 8 Ω.',
      value: (p) => (p.specs.kind === 'av' ? p.specs.power?.w8 ?? null : null),
      format: (p) => orDash(p.specs.kind === 'av' ? p.specs.power?.w8 : null, (w) => `${INT.format(w)} W`),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'roomCorrection',
      label: 'Room correction',
      describe: 'The automatic calibration it comes with.',
      value: (p) => (p.specs.kind === 'av' ? p.specs.roomCorrection?.v ?? null : null),
      format: (p) => orDash(p.specs.kind === 'av' ? p.specs.roomCorrection?.v : null, (s) => s),
      align: 'start',
      // With channels and power, the difference between receivers that
      // matters most once one drives your layout (plan §4).
      priority: 1,
      better: null,
    },
    {
      id: 'subOuts',
      label: 'Sub outs',
      describe: 'Subwoofer outputs.',
      value: (p) => (p.specs.kind === 'av' ? p.specs.preouts.v.subOuts : null),
      format: (p) => orDash(p.specs.kind === 'av' ? p.specs.preouts.v.subOuts : null, String),
      align: 'end',
      priority: 3,
      better: 'high',
    },
    {
      id: 'preouts',
      label: 'Pre-outs',
      describe: 'Channel groups it can send to a power amp.',
      value: (p) => (p.specs.kind === 'av' ? p.specs.preouts.v.groups.length : null),
      format: (p) => orDash(p.specs.kind === 'av' ? p.specs.preouts.v.groups.length : null, (n) => (n === 0 ? 'None' : String(n))),
      align: 'end',
      priority: 3,
      better: 'high',
    },
  ],
  filters: [
    PRICE_FILTER,
    typeFilter('receivers'),
    BRAND_FILTER,
    {
      kind: 'set',
      id: 'channels',
      label: 'Channels',
      value: (p) => {
        const c = avChannels(p);
        return c ? `${c.amp}/${c.proc}` : null;
      },
      optionLabel: (key) => {
        const [amp, proc] = key.split('/').map(Number);
        return channelsLabel(amp!, proc!);
      },
      rank: (key) => {
        const [amp, proc] = key.split('/').map(Number);
        return amp! * 100 + proc!;
      },
    },
    {
      kind: 'range',
      id: 'power',
      label: 'Power',
      value: (p) => (p.specs.kind === 'av' ? p.specs.power?.w8 ?? null : null),
      format: (v) => `${INT.format(v)} W`,
      step: 5,
    },
    {
      kind: 'set',
      id: 'roomCorrection',
      label: 'Room correction',
      value: (p) => (p.specs.kind === 'av' ? p.specs.roomCorrection?.v ?? null : null),
      optionLabel: (key) => key,
    },
    {
      kind: 'toggle',
      id: 'atmos',
      label: 'Dolby Atmos',
      test: (p) => p.specs.kind === 'av' && p.specs.heightDecoding.v.atmos,
    },
    {
      kind: 'set',
      id: 'subOuts',
      label: 'Sub outs',
      value: (p) => (p.specs.kind === 'av' ? String(p.specs.preouts.v.subOuts) : null),
      optionLabel: (key) => key,
      rank: Number,
    },
    CURRENT_FILTER,
  ],
  phoneColumns: ['channels', 'power', 'roomCorrection', 'subOuts'],
};

const amplifiers: PickerCategory = {
  key: 'amplifiers',
  noun: ['power amp', 'power amps'],
  columns: [
    {
      id: 'channels',
      label: 'Channels',
      value: (p) => (p.specs.kind === 'power_amp' ? p.specs.channels.v : null),
      format: (p) => orDash(p.specs.kind === 'power_amp' ? p.specs.channels.v : null, String),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'power8',
      label: 'Power 8 Ω',
      describe: 'Watts per channel into 8 Ω.',
      value: (p) => (p.specs.kind === 'power_amp' ? p.specs.power.w8 : null),
      format: (p) => orDash(p.specs.kind === 'power_amp' ? p.specs.power.w8 : null, (w) => `${INT.format(w)} W`),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'power4',
      label: 'Power 4 Ω',
      describe: 'Watts per channel into 4 Ω.',
      value: (p) => (p.specs.kind === 'power_amp' ? p.specs.power.w4 ?? null : null),
      format: (p) => orDash(p.specs.kind === 'power_amp' ? p.specs.power.w4 : null, (w) => `${INT.format(w)} W`),
      align: 'end',
      priority: 2,
      better: 'high',
    },
    {
      id: 'minLoad',
      label: 'Min load',
      describe: 'The lowest speaker impedance it is rated for.',
      value: (p) => (p.specs.kind === 'power_amp' ? p.specs.minImpedanceOhm.v : null),
      format: (p) => orDash(p.specs.kind === 'power_amp' ? p.specs.minImpedanceOhm.v : null, (v) => `${num(v)} Ω`),
      align: 'end',
      priority: 2,
      better: 'low',
    },
    {
      id: 'inputs',
      label: 'Inputs',
      value: (p) => (p.specs.kind === 'power_amp' ? ampInputsLabel(p.specs.inputs.v) : null),
      format: (p) => orDash(p.specs.kind === 'power_amp' ? p.specs.inputs.v : null, ampInputsLabel),
      align: 'start',
      priority: 3,
      better: null,
    },
  ],
  filters: [
    PRICE_FILTER,
    BRAND_FILTER,
    {
      kind: 'set',
      id: 'channels',
      label: 'Channels',
      value: (p) => (p.specs.kind === 'power_amp' ? String(p.specs.channels.v) : null),
      optionLabel: (key) => key,
      rank: Number,
    },
    {
      kind: 'range',
      id: 'power8',
      label: 'Power 8 Ω',
      value: (p) => (p.specs.kind === 'power_amp' ? p.specs.power.w8 : null),
      format: (v) => `${INT.format(v)} W`,
      step: 10,
    },
    {
      kind: 'set',
      id: 'minLoad',
      label: 'Min load',
      value: (p) => (p.specs.kind === 'power_amp' ? String(p.specs.minImpedanceOhm.v) : null),
      optionLabel: (key) => `${key} Ω`,
      rank: Number,
    },
    {
      kind: 'toggle',
      id: 'xlr',
      label: 'XLR inputs',
      test: (p) => p.specs.kind === 'power_amp' && p.specs.inputs.v.xlr,
    },
    CURRENT_FILTER,
  ],
  phoneColumns: ['channels', 'power8', 'power4', 'minLoad'],
};

const tvs: PickerCategory = {
  key: 'tvs',
  noun: ['TV', 'TVs'],
  columns: [
    {
      id: 'screen',
      label: 'Screen',
      describe: 'Diagonal.',
      value: (p) => (p.specs.kind === 'tv' ? p.specs.screenIn.v : null),
      format: (p) => orDash(p.specs.kind === 'tv' ? p.specs.screenIn.v : null, (v) => `${num(v)}″`),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'refresh',
      label: 'Refresh',
      describe: 'The fastest refresh rate it runs.',
      value: (p) => (p.specs.kind === 'tv' ? p.specs.refreshHz.v : null),
      format: (p) => orDash(p.specs.kind === 'tv' ? p.specs.refreshHz.v : null, (v) => `${Math.round(v)} Hz`),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'depth',
      label: 'Depth',
      describe: 'How far it stands off the wall, without a stand.',
      value: (p) => p.body.d,
      format: (p, ctx) => (ctx.units === 'imperial' ? `${num(p.body.d / MM_PER_IN)} in` : `${Math.round(p.body.d)} mm`),
      align: 'end',
      priority: 2,
      better: 'low',
    },
    {
      id: 'weight',
      label: 'Weight',
      value: (p) => p.weightKg?.v ?? null,
      format: (p, ctx) => orDash(p.weightKg?.v, (kg) => (ctx.units === 'imperial' ? `${Math.round(kg * 2.20462)} lb` : `${num(kg)} kg`)),
      align: 'end',
      priority: 3,
      better: 'low',
    },
  ],
  filters: [
    PRICE_FILTER,
    BRAND_FILTER,
    {
      kind: 'set',
      id: 'screen',
      label: 'Screen',
      value: (p) => (p.specs.kind === 'tv' ? num(p.specs.screenIn.v) : null),
      optionLabel: (key) => `${key}″`,
      rank: Number,
    },
    {
      kind: 'set',
      id: 'refresh',
      label: 'Refresh',
      value: (p) => (p.specs.kind === 'tv' ? String(Math.round(p.specs.refreshHz.v)) : null),
      optionLabel: (key) => `${key} Hz`,
      rank: Number,
    },
    CURRENT_FILTER,
  ],
  phoneColumns: ['screen', 'refresh', 'depth', 'weight'],
};

function imageLabel(r: NumRange): string {
  const lo = Math.round(r.min);
  const hi = Math.round(r.max);
  return lo === hi ? `${lo}″` : `${lo}–${hi}″`;
}

const projectors: PickerCategory = {
  key: 'projectors',
  noun: ['projector', 'projectors'],
  columns: [
    {
      id: 'brightness',
      label: 'Brightness',
      describe: 'ANSI lumens.',
      value: (p) => (p.specs.kind === 'projector' ? p.specs.lumensAnsi.v : null),
      format: (p) => orDash(p.specs.kind === 'projector' ? p.specs.lumensAnsi.v : null, (v) => `${INT.format(v)} lm`),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      value: (p) => (p.specs.kind === 'projector' ? RESOLUTION_RANK[p.specs.resolution.v] ?? null : null),
      format: (p) => orDash(p.specs.kind === 'projector' ? p.specs.resolution.v : null, (s) => s),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'throw',
      label: 'Throw ratio',
      describe: 'Distance ÷ picture width. A range is a zoom lens.',
      value: (p) => (p.specs.kind === 'projector' ? p.specs.throwRatio.v.min : null),
      format: (p) => orDash(p.specs.kind === 'projector' ? p.specs.throwRatio.v : null, (r) => (
        r.min === r.max ? num(r.min, 2) : `${num(r.min, 2)}–${num(r.max, 2)}`
      )),
      align: 'end',
      priority: 2,
      better: null,
    },
    {
      id: 'image',
      label: 'Image',
      describe: 'The 16:9 picture it fills from the back of your room.',
      value: (p, ctx) => ctx.imageIn?.(p)?.max ?? null,
      format: (p, ctx) => orDash(ctx.imageIn?.(p), imageLabel),
      align: 'end',
      priority: 1,
      better: 'high',
      unavailable: (ctx) => (ctx.imageIn ? null : 'Draw your room to see the picture each one fills.'),
    },
    sizeColumn(3),
  ],
  filters: [
    PRICE_FILTER,
    BRAND_FILTER,
    {
      kind: 'range',
      id: 'brightness',
      label: 'Brightness',
      value: (p) => (p.specs.kind === 'projector' ? p.specs.lumensAnsi.v : null),
      format: (v) => `${INT.format(v)} lm`,
      step: 100,
    },
    {
      kind: 'set',
      id: 'resolution',
      label: 'Resolution',
      value: (p) => (p.specs.kind === 'projector' ? p.specs.resolution.v : null),
      optionLabel: (key) => key,
      rank: (key) => RESOLUTION_RANK[key] ?? 0,
    },
    CURRENT_FILTER,
  ],
  phoneColumns: ['brightness', 'resolution', 'throw', 'image'],
};

const screens: PickerCategory = {
  key: 'screens',
  noun: ['screen', 'screens'],
  columns: [
    {
      id: 'diagonal',
      label: 'Size',
      describe: 'Diagonal.',
      value: (p) => (p.specs.kind === 'projector_screen' ? p.specs.diagonalIn.v : null),
      format: (p) => orDash(p.specs.kind === 'projector_screen' ? p.specs.diagonalIn.v : null, (v) => `${num(v)}″`),
      align: 'end',
      priority: 1,
      better: 'high',
    },
    {
      id: 'aspect',
      label: 'Aspect',
      value: (p) => (p.specs.kind === 'projector_screen' ? p.specs.aspect.v : null),
      format: (p) => orDash(p.specs.kind === 'projector_screen' ? p.specs.aspect.v : null, (s) => s),
      align: 'start',
      priority: 1,
      better: null,
    },
    {
      id: 'gain',
      label: 'Gain',
      describe: 'How much light it reflects back. 1 is neutral.',
      value: (p) => (p.specs.kind === 'projector_screen' ? p.specs.gain.v : null),
      format: (p) => orDash(p.specs.kind === 'projector_screen' ? p.specs.gain.v : null, (v) => num(v, 2)),
      align: 'end',
      priority: 2,
      better: null,
    },
    {
      id: 'format',
      label: 'Format',
      value: (p) => (p.specs.kind === 'projector_screen' ? SCREEN_FORMAT_LABEL[p.specs.format.v] : null),
      format: (p) => orDash(p.specs.kind === 'projector_screen' ? SCREEN_FORMAT_LABEL[p.specs.format.v] : null, (s) => s),
      align: 'start',
      priority: 2,
      better: null,
    },
    {
      id: 'soundThrough',
      label: 'Sound-through',
      describe: 'Acoustically transparent: speakers can stand behind it.',
      value: (p) => (p.specs.kind === 'projector_screen' ? Number(p.specs.acousticallyTransparent.v) : null),
      // A spec-sheet tick: "Yes", or a dash for a published "no".
      format: (p) => (p.specs.kind === 'projector_screen' && p.specs.acousticallyTransparent.v ? 'Yes' : NOT_PUBLISHED),
      align: 'start',
      priority: 2,
      better: 'high',
    },
  ],
  filters: [
    PRICE_FILTER,
    BRAND_FILTER,
    {
      kind: 'set',
      id: 'diagonal',
      label: 'Size',
      value: (p) => (p.specs.kind === 'projector_screen' ? num(p.specs.diagonalIn.v) : null),
      optionLabel: (key) => `${key}″`,
      rank: Number,
    },
    {
      kind: 'set',
      id: 'aspect',
      label: 'Aspect',
      value: (p) => (p.specs.kind === 'projector_screen' ? p.specs.aspect.v : null),
      optionLabel: (key) => key,
      rank: fixedRank(ASPECT_ORDER),
    },
    {
      kind: 'set',
      id: 'format',
      label: 'Format',
      value: (p) => (p.specs.kind === 'projector_screen' ? p.specs.format.v : null),
      optionLabel: (key) => SCREEN_FORMAT_LABEL[key as ScreenFormat] ?? key,
      rank: fixedRank(SCREEN_FORMAT_ORDER),
    },
    {
      kind: 'toggle',
      id: 'soundThrough',
      label: 'Sound-through',
      test: (p) => p.specs.kind === 'projector_screen' && p.specs.acousticallyTransparent.v,
    },
    CURRENT_FILTER,
  ],
  phoneColumns: ['diagonal', 'aspect', 'gain', 'format'],
};

export const PICKER_CATEGORIES: Record<CategoryKey, PickerCategory> = {
  speakers,
  subwoofers,
  receivers,
  amplifiers,
  tvs,
  projectors,
  screens,
};

export function pickerCategory(key: CategoryKey): PickerCategory {
  return PICKER_CATEGORIES[key];
}

/** "98 speakers", "1 power amp". */
export function countLabel(category: CategoryKey, n: number): string {
  const [one, many] = PICKER_CATEGORIES[category].noun;
  return `${INT.format(n)} ${n === 1 ? one : many}`;
}

// ── Filtering ───────────────────────────────────────────────────────────────

export function emptyFilterState(): PickerFilterState {
  return { query: '', fitsOnly: true, ranges: {}, sets: {}, toggles: {} };
}

/**
 * Where a picker starts: nothing narrowed, except the types a slot takes
 * (front speakers open on bookshelves and towers) and a query carried in from
 * a search box. Types the category does not list are dropped.
 */
export function defaultFilterState(
  category: CategoryKey,
  opts: { types?: readonly string[]; query?: string } = {},
): PickerFilterState {
  const state = { ...emptyFilterState(), query: opts.query ?? '' };
  const hasTypeFilter = PICKER_CATEGORIES[category].filters.some((f) => f.id === 'type');
  const known = new Set(categoryByKey(category).types.map((t) => t.type));
  const types = (opts.types ?? []).filter((t) => known.has(t));
  return hasTypeFilter && types.length > 0 ? { ...state, sets: { type: types } } : state;
}

function passes(p: Product, def: PickerFilterDef, state: PickerFilterState, ctx: PickerContext): boolean {
  if (def.kind === 'range') {
    const range = state.ranges[def.id];
    if (!range) return true;
    const v = def.value(p, ctx);
    // A narrowed range asks for a value inside it: "not published" is not.
    return v != null && v >= range[0] && v <= range[1];
  }
  if (def.kind === 'set') {
    const checked = state.sets[def.id];
    if (!checked || checked.length === 0) return true;
    const v = def.value(p);
    return v != null && checked.includes(v);
  }
  return !state.toggles[def.id] || def.test(p);
}

/** Hidden only when the room says it does not fit — unjudged stays. */
function fitsTheSystem(p: Product, ctx: PickerContext): boolean {
  return ctx.fits?.(p) !== false;
}

function passesFits(p: Product, state: PickerFilterState, ctx: PickerContext): boolean {
  return !state.fitsOnly || fitsTheSystem(p, ctx);
}

function queryMatches(products: readonly Product[], query: string): ReadonlySet<string> | null {
  if (!query.trim()) return null;
  return new Set(searchCatalog(products, query, null).map((p) => p.id));
}

/** The products every active filter (and the search) lets through, in input order. */
export function applyFilters(
  products: readonly Product[],
  category: CategoryKey,
  state: PickerFilterState,
  ctx: PickerContext,
): Product[] {
  const defs = PICKER_CATEGORIES[category].filters;
  const matched = queryMatches(products, state.query);
  return products.filter((p) => (!matched || matched.has(p.id))
    && passesFits(p, state, ctx)
    && defs.every((d) => passes(p, d, state, ctx)));
}

export interface FacetOption {
  key: string;
  label: string;
  /** Products with this option that every OTHER filter lets through. */
  count: number;
}

export interface Facets {
  /** Every option the category's products have, in display order — a
   *  checked option never disappears just because nothing else matches it. */
  sets: Record<string, FacetOption[]>;
  /** How many products each toggle would leave, given every other filter. */
  toggles: Record<string, number>;
  /** Products every filter but "fits" lets through: "98 speakers". */
  matching: number;
  /** How many of those fit the system: "· 74 fit". null: nothing judged. */
  fitting: number | null;
}

/** The fits filter's id among the ones a product can fail; no def uses it. */
const FITS_ID = '\u0000fits';

/**
 * PCPartPicker's counts beside each filter option: how many products you
 * would see with that option, given everything ELSE you have chosen — a
 * filter's own selection never zeroes its siblings.
 */
export function facetCounts(
  products: readonly Product[],
  category: CategoryKey,
  state: PickerFilterState,
  ctx: PickerContext,
): Facets {
  const defs = PICKER_CATEGORIES[category].filters;
  const matched = queryMatches(products, state.query);
  const inQuery = matched ? products.filter((p) => matched.has(p.id)) : products;
  // Per product, the filters it fails — so "every filter but this one" is a lookup.
  const failing = new Map(inQuery.map((p) => {
    const f = new Set(defs.filter((d) => !passes(p, d, state, ctx)).map((d) => d.id));
    if (!passesFits(p, state, ctx)) f.add(FITS_ID);
    return [p, f];
  }));
  const passesAllBut = (p: Product, id: string) => {
    const f = failing.get(p)!;
    return f.size === 0 || (f.size === 1 && f.has(id));
  };

  const others = inQuery.filter((p) => passesAllBut(p, FITS_ID));
  const judged = !!ctx.fits && products.some((p) => ctx.fits!(p) !== null);
  const facets: Facets = {
    sets: {},
    toggles: {},
    matching: others.length,
    fitting: judged ? others.filter((p) => fitsTheSystem(p, ctx)).length : null,
  };
  for (const def of defs) {
    if (def.kind === 'set') {
      const counts = new Map<string, number>();
      for (const p of products) {
        const key = def.value(p);
        if (key != null && !counts.has(key)) counts.set(key, 0);
      }
      for (const p of inQuery) {
        const key = def.value(p);
        if (key != null && passesAllBut(p, def.id)) counts.set(key, counts.get(key)! + 1);
      }
      const rank = def.rank;
      facets.sets[def.id] = [...counts.entries()]
        .sort(([a], [b]) => (rank ? rank(a) - rank(b) : 0) || a.localeCompare(b, 'en', { numeric: true }))
        .map(([key, count]) => ({ key, label: def.optionLabel(key), count }));
    } else if (def.kind === 'toggle') {
      facets.toggles[def.id] = inQuery.filter((p) => passesAllBut(p, def.id) && def.test(p)).length;
    }
  }
  return facets;
}

/** A range filter's full extent over `products`, rounded out to its step;
 *  null when fewer than two values differ (nothing to narrow). */
export function rangeBounds(
  products: readonly Product[],
  def: RangeFilterDef,
  ctx: PickerContext,
): NumRange | null {
  let min = Infinity;
  let max = -Infinity;
  for (const p of products) {
    const v = def.value(p, ctx);
    if (v == null) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!(max > min)) return null;
  return { min: Math.floor(min / def.step) * def.step, max: Math.ceil(max / def.step) * def.step };
}

/**
 * Whether `def` can narrow `products` at all: a range with two different
 * values, a set with two options, a toggle some products pass and some fail.
 * A filter that cannot change the list is not offered (every TV is current,
 * so "Hide discontinued" has nothing to hide among TVs).
 */
export function filterVaries(products: readonly Product[], def: PickerFilterDef, ctx: PickerContext): boolean {
  if (def.kind === 'range') return rangeBounds(products, def, ctx) !== null;
  if (def.kind === 'set') {
    const keys = new Set<string>();
    for (const p of products) {
      const key = def.value(p);
      if (key != null) keys.add(key);
      if (keys.size > 1) return true;
    }
    return false;
  }
  const passing = products.filter((p) => def.test(p)).length;
  return passing > 0 && passing < products.length;
}

/** How many filters are narrowing the list — the search box and the fits
 *  switch (a standing mode the count line reports) not included. */
export function activeFilterCount(state: PickerFilterState): number {
  return Object.keys(state.ranges).length
    + Object.values(state.sets).filter((s) => s.length > 0).length
    + Object.values(state.toggles).filter(Boolean).length;
}

// ── Sorting ─────────────────────────────────────────────────────────────────

function sortValueOf(category: CategoryKey, key: PickerSortKey, ctx: PickerContext): (p: Product) => SortValue {
  if (key === 'name') return (p) => `${p.brand} ${p.model}`;
  if (key === 'price') return (p) => pickerPrice(p, ctx);
  const column = PICKER_CATEGORIES[category].columns.find((c) => c.id === key);
  if (!column) throw new Error(`parts picker: ${category} has no column "${key}"`);
  return (p) => column.value(p, ctx);
}

/**
 * Sorted by `key` — 'name', 'price' or a column id. Stable (ties keep their
 * input order), and a value the maker does not publish sorts LAST in both
 * directions: flipping to descending must not put every "—" on top.
 */
export function sortProducts(
  products: readonly Product[],
  category: CategoryKey,
  key: PickerSortKey,
  dir: SortDir,
  ctx: PickerContext,
): Product[] {
  const valueOf = sortValueOf(category, key, ctx);
  const sign = dir === 'asc' ? 1 : -1;
  return products
    .map((p, i) => ({ p, i, v: valueOf(p) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) {
        if (a.v === b.v) return a.i - b.i;
        return a.v === null ? 1 : -1;
      }
      const c = typeof a.v === 'number' && typeof b.v === 'number'
        ? a.v - b.v
        : String(a.v).localeCompare(String(b.v), 'en', { numeric: true });
      return sign * c || a.i - b.i;
    })
    .map((d) => d.p);
}

/** The direction a first click on a column shows: its better end first. */
export function firstSortDir(category: CategoryKey, key: PickerSortKey): SortDir {
  if (key === 'name' || key === 'price') return 'asc';
  const column = PICKER_CATEGORIES[category].columns.find((c) => c.id === key);
  return column?.better === 'high' ? 'desc' : 'asc';
}
